import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  decryptIntegrationToken,
  validateIntegrationPayload
} from '../../utils/integrationCrypto';
import './IntegrationEntry.css';

/**
 * Integration Entry Point
 * URL format: /?data=<encrypted_data>
 *
 * Workflow:
 * - Extract encrypted data from URL parameter
 * - Decrypt data in the browser using Web Crypto API
 * - Validate all mandatory fields
 * - Store decrypted data in sessionStorage
 * - Redirect to dedicated integration ticket form without changing the URL
 */
const IntegrationEntry = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading | success | error
  const [error, setError] = useState('');
  const hasAttemptedRef = React.useRef(false);

  const SHARED_SECRET =
    process.env.REACT_APP_INTEGRATION_SHARED_SECRET ||
    'ITSM_Vardaan_Integration_v1#2026';

  useEffect(() => {
    if (hasAttemptedRef.current) return;
    hasAttemptedRef.current = true;

    const processIntegration = async () => {
      const data = searchParams.get('data');

      if (!data) {
        // No integration data — this is a normal visit. Redirect to login.
        navigate('/login', { replace: true });
        return;
      }

      try {
        const payload = await decryptIntegrationToken(data, SHARED_SECRET);
        validateIntegrationPayload(payload);

        // name/email may be empty when the user comes from an external
        // application's login page (not logged in yet). In that case we
        // cannot run the check-email lookup, so we assume a new user and
        // let them fill name/email manually on the ticket form.
        const hasEmail = !!(payload.email && String(payload.email).includes('@'));

        let isNewUser = true;
        let hasPassword = false;
        if (hasEmail) {
          const checkResponse = await fetch('/api/auth/customers/check-email', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email: payload.email })
          });
          const checkData = await checkResponse.json();
          isNewUser = !checkData.exists;
          hasPassword = checkData.hasPassword;
        }

        // All users (existing and new) proceed to ticket creation
        // The difference is handled after ticket submission:
        // - New users: auto-logout and redirect to customer-access for email verification
        // - Existing users: stay logged in and show dashboard
        const integrationPayload = {
          email: payload.email || '',
          name: payload.name || '',
          product: payload.product,
          module: payload.module,
          organization_name: payload.organization_name,
          parent_organization_name: payload.parent_organization_name,
          timestamp: new Date().toISOString(),
          isNewUser,
          hasPassword
        };

        sessionStorage.setItem('integrationPayload', JSON.stringify(integrationPayload));

        // Navigate to ticket creation form without exposing decrypted data in URL
        navigate('/ticket-create', { replace: true });
      } catch (err) {
        console.error('Integration decryption error:', err);
        setError(err.message || 'Invalid or expired integration data');
        setStatus('error');
      }
    };

    processIntegration();
  }, [searchParams, navigate, SHARED_SECRET]);

  if (status === 'loading') {
    return (
      <div className="integration-entry">
        <div className="integration-entry-content">
          <div className="integration-entry-spinner" />
          <h2>Processing Integration...</h2>
          <p>Decrypting data and preparing your ticket creation page</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="integration-entry">
        <div className="integration-entry-content integration-entry-error">
          <h2>Integration Error</h2>
          <p className="integration-entry-error-msg">{error}</p>
          <a href="/" className="integration-entry-btn">Go to Home</a>
        </div>
      </div>
    );
  }

  return null;
};

export default IntegrationEntry;
